/**
 * RemoteMemberMarkers
 *
 * Internal part of `UserLocationLayer`: renders one marker per remote workspace
 * member broadcasting a position (`presence.memberLocations`, excluding the
 * current user). Member display names / avatars are resolved from the shared
 * workspace member cache, which is only fetched when at least one remote member
 * is actually broadcasting (no eager fetch on a bare map mount).
 *
 * Subscribes only to the STABLE list of remote member ids — each marker reads
 * its own position via an atomic, id-keyed selector — so a single member's fix
 * re-renders only that marker, not the whole list.
 *
 * IMPORTANT: this component (and its presence/member hooks) is mounted ONLY on
 * the authenticated map. The public map renders `UserLocationLayer localOnly`,
 * which never mounts this part — so none of these hooks run there.
 *
 * @module components/map/UserLocationLayer/parts/RemoteMemberMarkers
 */
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { useWorkspaceMemberSuggestions } from '@/hooks/useWorkspaceMemberSuggestions';
import { useGeoMarkupStore } from '@/store';
import { getMemberLabel } from '@/utils';

import { MemberLocationMarker } from './MemberLocationMarker';

export const RemoteMemberMarkers = memo(function RemoteMemberMarkers() {
  const { t } = useTranslation();
  const currentUserId = useGeoMarkupStore(
    (state) => state.auth.user?.id ?? null
  );

  // Subscribe only to the stable SET of broadcasting member ids — replacing one
  // member's location entry does not change this key list, so the list (and the
  // other markers) stay put; only the moving marker re-renders via its own
  // id-keyed selector.
  const memberIds = useGeoMarkupStore(
    useShallow((state) => Object.keys(state.presence.memberLocations))
  );

  // Remote members only — exclude the local user's own broadcast.
  const remoteMemberIds = useMemo(
    () => memberIds.filter((memberId) => memberId !== currentUserId),
    [memberIds, currentUserId]
  );

  const hasRemote = remoteMemberIds.length > 0;

  // "No Eager Load, Derive From Loaded": only resolve member names/avatars
  // (which triggers GET /workspaces/{id}/members) when remote members are
  // actually broadcasting a position — never on a bare map mount.
  const { members } = useWorkspaceMemberSuggestions({ enabled: hasRemote });

  // Map userId → { displayName, avatarUrl } for fast lookup when rendering.
  const memberInfoById = useMemo(() => {
    const lookup = new Map<
      string,
      { displayName: string; avatarUrl: string | null }
    >();
    members.forEach((member) => {
      lookup.set(member.userId, {
        displayName: getMemberLabel(member),
        avatarUrl: member.avatarUrl,
      });
    });
    return lookup;
  }, [members]);

  return (
    <>
      {remoteMemberIds.map((memberId) => {
        const info = memberInfoById.get(memberId);
        // Never surface the raw UUID: fall back to a neutral placeholder so the
        // avatar initials + tooltip stay readable until the member cache loads.
        const displayName = info?.displayName ?? t('comments.anonymousAuthor');
        return (
          <MemberLocationMarker
            key={memberId}
            userId={memberId}
            displayName={displayName}
            avatarUrl={info?.avatarUrl}
          />
        );
      })}
    </>
  );
});
